import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Link } from "react-router-dom";

import { webRoute } from "../../config/route";
import { services } from "../../constants/services";
import { buildServiceUrl } from "../../config/urls";

const NavItemControl: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setServicesOpen(false);
  }, []);

  useEffect(() => {
    if (isDesktop) closeDrawer();
  }, [isDesktop, closeDrawer]);

  const toggleServices = useCallback(() => {
    setServicesOpen((prev) => !prev);
  }, []);

  return (
    <>
      {/* ================= Desktop Navigation ================= */}
      <Box component="nav" aria-label="Main navigation" className="header-nav">
        {webRoute
          .filter((r) => r.key !== "NotFound")
          .map((route) =>
            route.hasDropdown ? (
              <Box key={route.key} className="nav-dropdown">
                <span className="nav-link" role="button" aria-haspopup="true">
                  Services
                </span>

                <Box className="nav-dropdown-menu" role="menu">
                  {services.map((service) => (
                    <Link
                      key={service.slug}
                      to={buildServiceUrl(service.slug)}
                      className="nav-dropdown-item"
                      role="menuitem"
                    >
                      {service.title}
                    </Link>
                  ))}
                </Box>
              </Box>
            ) : (
              <Link key={route.key} to={route.path} className="nav-link">
                {route.label}
              </Link>
            )
          )}
      </Box>

      {/* ================= Mobile Menu Icon ================= */}
      <IconButton
        className="menu-icon"
        onClick={() => setDrawerOpen(true)}
        aria-label="Open navigation menu"
        aria-expanded={drawerOpen}
      >
        <MenuIcon />
      </IconButton>

      {/* ================= Mobile Drawer ================= */}
      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
        <Box className="mobile-drawer" role="navigation" aria-label="Mobile navigation">
          <Box className="drawer-header">
            <IconButton onClick={closeDrawer} aria-label="Close navigation menu">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box className="drawer-nav">
            {webRoute
              .filter((r) => r.key !== "NotFound")
              .map((route) =>
                route.hasDropdown ? (
                  <Box key={route.key}>
                    <Box
                      className="drawer-link drawer-service-toggle"
                      onClick={toggleServices}
                      role="button"
                      aria-expanded={servicesOpen}
                      aria-haspopup="true"
                    >
                      Services
                      {servicesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>

                    <Collapse in={servicesOpen}>
                      <Box className="drawer-submenu">
                        {services.map((service) => (
                          <Link
                            key={service.slug}
                            to={buildServiceUrl(service.slug)}
                            className="drawer-sublink"
                            onClick={closeDrawer}
                          >
                            {service.title}
                          </Link>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                ) : (
                  <Link
                    key={route.key}
                    to={route.path}
                    className="drawer-link"
                    onClick={closeDrawer}
                  >
                    {route.label}
                  </Link>
                )
              )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default React.memo(NavItemControl);
